import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import Api from '../../Api';
import { withAuth } from '../../hoc/Auth';
import Icon from '../Utils/Icon';
import Dropdown from '../Utils/Dropdown';
import Support from './Support';
import { createDefaultHeaderMenuConfig } from './menuConfig';

import classes from './styles.module.css';

const ACCESS_LIST = {
    TRANSACTION_CANCELLING: 1,
    TRANSACTION_LIMIT_SETTING: 2,
    STATISTICS_ACCESS: 4,
    MAIL_ALERT_SETTING: 8,
    PAYOUT_REGISTRY_PROCESS: 16,
    PAYOUT_REGISTRY_VIEW: 32,
    PAYOUT_REGISTRY_REPORT: 64,
    PAYOUT_REGISTRIES_LIST: 128,
    PREPAID_CARDS_VIEW: 256,
    PREPAID_CARDS_ACTIVATE: 512,
    STATISTICS_REPORT_1C_VIEW: 1024,
    ACCESS_PAYOUTS: 2048,
    ACCESS_METAL: 4096,
    ACCESS_SBP_REGISTRY: 8192,
    ACCESS_SBP_PAYOUT: 16384,
    ACCESS_ANTIFRAUD_FILE: 32768,
    ACCESS_ANTIFRAUD_SINGLE: 65536,
};

function matchesPath(pathname, target) {
    return pathname === target || pathname.startsWith(target + '/');
}

function useViewportWidth() {
    const [viewportWidth, setViewportWidth] = useState(() => {
        if (typeof window === 'undefined') return 0;
        return window.innerWidth;
    });

    useEffect(() => {
        function update() {
            if (typeof window === 'undefined') return;
            setViewportWidth(window.innerWidth);
        }

        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    return viewportWidth;
}

function resolveTitle(title, context) {
    if (typeof title === 'function') {
        return resolveTitle(title(context), context);
    }

    if (React.isValidElement(title)) {
        return title;
    }

    if (title && typeof title === 'object' && title.id) {
        return <FormattedMessage id={title.id} defaultMessage={title.defaultMessage || title.id} />;
    }

    return title;
}

/**
 * Приписка у пункта меню: «бета» или число непрочитанных.
 *
 * Значение считается заранее: `item.badge` — функция, то есть всегда
 * истинна, и проверка «есть ли badge» рисовала пустой кружок даже когда
 * считать нечего. Отсюда прочерк рядом с пунктом «Чаты».
 */
function renderBadge(item, context) {
    if (!item.badge) return null;

    const value = resolveTitle(item.badge, context);
    if (value === null || value === undefined || value === '') return null;

    return <span className={classes.Badge}>{value}</span>;
}

function getAccessMask(access) {
    if (!Array.isArray(access)) return 0;
    return access.reduce((sum, permission) => sum + ACCESS_LIST[permission], 0);
}

function hasAccess(item, context) {
    if (item.access === undefined || item.access === null || item.access === true) return true;

    const accessMask = getAccessMask(item.access);

    if (context.globalPrivileges !== null && context.globalPrivileges !== undefined) {
        return (accessMask & context.globalPrivileges) > 0;
    }

    let currentAccess = 0;
    if (context.accountAccessSettings) {
        context.accountAccessSettings.forEach((setting) => {
            if (item.merchantUUID !== undefined) {
                if (setting.merchantUUID === item.merchantUUID) currentAccess = setting.privileges;
            } else if ((accessMask & setting.privileges) > 0) {
                currentAccess = setting.privileges;
            }
        });
    }

    return (accessMask & currentAccess) > 0;
}

function renderNavContent(item, context, variant, onClick) {
    if (item.type === 'link') {
        const isTicketsLink = item.special === 'tickets';
        const itemHasAccess = hasAccess(item, context);

        if (!itemHasAccess && item.accessDenied === 'hide') {
            return null;
        }

        const baseClassName = isTicketsLink
            ? classes.TicketsLink
            : variant === 'drawer'
              ? classes.DrawerLink
              : classes.Link;
        const linkClassName = itemHasAccess ? baseClassName : `${baseClassName} ${classes.LinkDisabled}`;
        const handleClick = (event) => {
            if (!itemHasAccess) {
                event.preventDefault();
                return;
            }

            if (onClick) onClick(event);
        };

        return (
            <NavLink
                activeClassName={isTicketsLink ? classes.TicketsActive : variant === 'drawer' ? classes.Active : classes.Active}
                className={linkClassName}
                exact={item.exact !== false}
                to={item.to}
                onClick={handleClick}
            >
                <Icon
                    className={isTicketsLink ? classes.IconMessage : classes.IconLeft}
                    icon={itemHasAccess ? item.icon : 'lock'}
                    size={isTicketsLink ? '20px' : variant === 'drawer' ? '15px' : '16px'}
                />
                <span className={classes.LinkText}>{item.title ? resolveTitle(item.title, context) : null}</span>
                {/* Число непрочитанных: пустое значение не рисуем вовсе,
                    иначе рядом с пунктом висит пустой кружок. */}
                {renderBadge(item, context)}
            </NavLink>
        );
    }

    if (item.type === 'button') {
        const handleClick = item.action === 'logout' ? context.logout : onClick;

        if (variant === 'desktop') {
            return (
                <li key={item.key} className={classes.Link} onClick={handleClick}>
                    <Icon className={classes.IconLeft} icon={item.icon} size={'18px'} />
                    <span className={`${classes.LinkText} ${classes.ProfileText}`}>{resolveTitle(item.title, context)}</span>
                </li>
            );
        }

        return (
            <button type="button" className={variant === 'drawer' ? classes.DrawerButton : classes.Link} onClick={handleClick}>
                <Icon className={classes.IconLeft} icon={item.icon} size={variant === 'drawer' ? '18px' : '16px'} />
                <span className={`${classes.LinkText} ${classes.ProfileText}`}>{resolveTitle(item.title, context)}</span>
            </button>
        );
    }

    if (item.type === 'custom') {
        return item.render(context);
    }

    return null;
}

function renderListItem(item, context, variant, onClick) {
    if (!item) return null;

    if (item.type === 'link') {
        const content = renderNavContent(item, context, variant, onClick);
        return content ? <li key={item.key}>{content}</li> : null;
    }

    if (item.type === 'button') {
        return <li key={item.key}>{renderNavContent(item, context, variant, onClick)}</li>;
    }

    if (item.type === 'custom') {
        return <React.Fragment key={item.key}>{renderNavContent(item, context, variant, onClick)}</React.Fragment>;
    }

    return null;
}

function renderDropdownChildren(items, context, onClick, variant) {
    return items.map((item) => renderListItem(item, context, variant, onClick));
}

function renderGroupContent(group, context, onClick, variant) {
    if (group.content === 'support') {
        return <Support data={context.manager} />;
    }

    return <ul className={classes.DrawerSubmenu}>{renderDropdownChildren(group.items || [], context, onClick, variant)}</ul>;
}

function getActiveGroupKey(pathname, groups) {
    const activeGroup = groups.find(
        (group) => Array.isArray(group.paths) && group.paths.some((path) => matchesPath(pathname, path)),
    );
    return activeGroup ? activeGroup.key : null;
}

function renderDesktopGroup(group, context, onClick) {
    const resolvedTitle = resolveTitle(group.title, context);
    // Точка у профиля: раздел чатов спрятан внутри этого меню, и без метки
    // снаружи новое сообщение остаётся незамеченным.
    const hasChatMark = group.key === 'profile' && Number(context.secretChatUnread || 0) > 0;
    const titleNode =
        group.key === 'profile' ? (
            <span className={classes.ProfileTitle}>
                {resolvedTitle}
                {hasChatMark ? <span className={classes.UnreadDot} /> : null}
            </span>
        ) : (
            resolvedTitle
        );

    return (
        <Dropdown
            key={group.key}
            title={titleNode}
            icon={group.icon}
            isHeader={group.isHeader}
            isFullWidth={group.isFullWidth}
            submenuVariant={group.submenuVariant}
            useLocale={false}
        >
            {group.content === 'support'
                ? renderGroupContent(group, context, onClick, 'desktop')
                : renderDropdownChildren(group.items || [], context, onClick, 'desktop')}
        </Dropdown>
    );
}

function getTicketsItem(items) {
    return items.find((item) => item.type === 'link' && item.special === 'tickets') || null;
}

function renderDrawerGroup(group, context, openedSection, setOpenedSection, onClick) {
    const isOpen = openedSection === group.key;
    const isActive = Array.isArray(group.paths) && group.paths.some((path) => matchesPath(context.pathname, path));

    return (
        <li key={group.key}>
            <button
                type="button"
                className={`${classes.DrawerToggle}${isActive ? ' ' + classes.DrawerToggleActive : ''}`}
                onClick={() => setOpenedSection((current) => (current === group.key ? null : group.key))}
                aria-expanded={isOpen}
            >
                <span className={classes.DrawerToggleLabel}>
                    <Icon className={classes.IconLeft} icon={group.icon} size={'15px'} />
                    <span className={classes.LinkText}>{resolveTitle(group.title, context)}</span>
                </span>
                <Icon className={classes.IconRight} icon="cheveron-down" size={'16px'} />
            </button>

            {isOpen && renderGroupContent(group, context, onClick, 'drawer')}
        </li>
    );
}

function HeaderMenu({
    token,
    logout,
    isAuthorized,
    adminPrivileges,
    accountAccessSettings,
    globalPrivileges,
    menuConfig,
    mode = 'top',
}) {
    const location = useLocation();
    const intl = useIntl();
    const viewportWidth = useViewportWidth();
    const isMobile = viewportWidth > 0 ? viewportWidth <= 768 : false;
    const [user, setUser] = useState(null);
    const [manager, setManager] = useState({});
    const [, setLoading] = useState(true);
    const [, setError] = useState(false);
    const [isBurgerOpen, setIsBurgerOpen] = useState(false);
    const [openedSection, setOpenedSection] = useState(null);

    const resolvedMenuConfig = useMemo(
        () => menuConfig || createDefaultHeaderMenuConfig({ adminPrivileges }),
        [adminPrivileges, menuConfig],
    );

    const isTopMode = mode !== 'nav';

    useEffect(() => {
        if (!isTopMode || !isAuthorized) return undefined;

        const users = new Api({ token }).getUserData();

        if (adminPrivileges) {
            users
                .then((data) => {
                    setUser(data);
                    setLoading(false);
                    setError(false);
                })
                .catch((err) => {
                    console.error('Error loading admin data:', err);
                    setLoading(false);
                    setError(true);
                });

            return undefined;
        }

        const managerPromise = new Api({ token }).getPersonalManager();

        Promise.all([users, managerPromise])
            .then((data) => {
                setUser(data[0]);
                setManager(data[1]);
                setLoading(false);
                setError(false);
            })
            .catch((err) => {
                console.error('Error loading user data:', err);
                setLoading(false);
                setError(true);
            });

        return undefined;
    }, [adminPrivileges, isAuthorized, isTopMode, token]);
    // Непрочитанные сообщения секретного чата. Раздел лежит внутри меню
    // профиля, поэтому без пометки человек узнавал бы об ответе, только
    // заглянув туда сам, — а переписка живёт сутки и исчезает.
    const [secretChatUnread, setSecretChatUnread] = useState(0);

    useEffect(() => {
        if (!isAuthorized || !token) return undefined;

        let cancelled = false;

        const load = () => {
            const api = new Api({ token });
            // Сотрудник тоже бывает собеседником: у него есть и свои чаты, и
            // чужие. Спрашиваем обе стороны и складываем — иначе админ видел
            // только клиентские сообщения и пропускал ответы на свои.
            const requests = [api.getSecretChatsUnreadCount().catch(() => null)];

            if (adminPrivileges) {
                requests.push(api.getAdminSecretChatsUnreadCount().catch(() => null));
            }

            Promise.all(requests)
                .then((answers) => {
                    if (cancelled) return;
                    setSecretChatUnread(
                        answers.reduce((sum, data) => sum + Number((data && data.count) || 0), 0)
                    );
                })
                .catch(() => {
                    // Молча: пометка вспомогательная, ронять шапку из-за неё нельзя.
                });
        };

        load();
        const timer = setInterval(load, 15000);

        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [adminPrivileges, isAuthorized, token]);


    useEffect(() => {
        if (!isTopMode) return undefined;
        setIsBurgerOpen(false);
        setOpenedSection(null);
    }, [isTopMode, location.pathname]);

    const drawerSections = resolvedMenuConfig.drawer.sections || [];
    const drawerFooterItems = resolvedMenuConfig.drawer.footerItems || [];
    const drawerFooterTitle = resolvedMenuConfig.drawer.footerTitle;

    useEffect(() => {
        if (!isTopMode) return undefined;
        if (!isBurgerOpen || !isMobile) return undefined;

        setOpenedSection(getActiveGroupKey(location.pathname, drawerSections));
        return undefined;
    }, [drawerSections, isBurgerOpen, isMobile, isTopMode, location.pathname]);

    useEffect(() => {
        if (!isTopMode) return undefined;
        if (!isBurgerOpen || !isMobile) {
            document.body.style.overflow = '';
            return undefined;
        }

        document.body.style.overflow = 'hidden';

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setIsBurgerOpen(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = '';
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isBurgerOpen, isMobile, isTopMode]);

    if (isTopMode && !isAuthorized) {
        return null;
    }

    const closeDrawer = () => setIsBurgerOpen(false);
    if (!isTopMode) {
        const desktopLeftItems = resolvedMenuConfig.desktop.leftItems || [];
        const navContext = {
            pathname: location.pathname,
            logout,
            adminPrivileges,
            accountAccessSettings,
            globalPrivileges,
            secretChatUnread,
        };

        return (
            <div className={`${classes.Root}${adminPrivileges ? ' ' + classes.AdminMode : ''} ${classes.NavMode}`}>
                <ul className={classes.Menu}>
                    {desktopLeftItems.map((item) => {
                        if (item.type === 'group' || item.type === 'dropdown') {
                            return renderDesktopGroup(item, navContext, closeDrawer);
                        }

                        return renderListItem(item, navContext, 'desktop', closeDrawer);
                    })}
                </ul>
            </div>
        );
    }

    const safeUser = user || {};
    const profileName =
        [safeUser.firstName, safeUser.lastName].filter(Boolean).join(' ') ||
        intl.formatMessage({ id: 'HeaderMenu.list.profile', defaultMessage: 'Профиль' });
    const desktopRightItems = resolvedMenuConfig.desktop.rightItems || [];
    const ticketsItem = getTicketsItem(desktopRightItems);
    const context = {
        user: safeUser,
        manager,
        profileName,
        pathname: location.pathname,
        logout,
        adminPrivileges,
        accountAccessSettings,
        globalPrivileges,
        secretChatUnread,
    };

    return (
        <div className={`${classes.Root}${adminPrivileges ? ' ' + classes.AdminMode : ''}`}>
            {desktopRightItems.length > 0 && (
                <ul className={classes.DesktopActions}>
                    {desktopRightItems.map((item) => {
                        if (item.type === 'group' || item.type === 'dropdown') {
                            return renderDesktopGroup(item, context, closeDrawer);
                        }

                        return renderListItem(item, context, 'desktop', closeDrawer);
                    })}
                </ul>
            )}

            <div className={classes.MobileActions}>
                {ticketsItem && renderNavContent(ticketsItem, context, 'desktop', closeDrawer)}

                <button
                    type="button"
                    className={classes.BurgerButton}
                    onClick={() => setIsBurgerOpen((value) => !value)}
                    aria-label={
                        isBurgerOpen
                            ? intl.formatMessage({ id: 'HeaderMenu.list.closeMenu', defaultMessage: 'Закрыть меню' })
                            : intl.formatMessage({ id: 'HeaderMenu.list.openMenu', defaultMessage: 'Открыть меню' })
                    }
                    aria-expanded={isBurgerOpen}
                >
                    <Icon className={classes.BurgerIcon} icon={isBurgerOpen ? 'close' : 'menu'} size={'20px'} />
                </button>
            </div>

            {isMobile && isBurgerOpen && (
                <div className={classes.Overlay} onClick={closeDrawer}>
                    <div className={classes.Drawer} onClick={(event) => event.stopPropagation()}>
                        <div className={classes.DrawerSection}>
                            <ul className={classes.DrawerMenu}>
                                {resolvedMenuConfig.drawer.sections.map((item) => {
                                    if (item.type === 'group' || item.type === 'dropdown') {
                                        return renderDrawerGroup(item, context, openedSection, setOpenedSection, closeDrawer);
                                    }

                                    return renderListItem(item, context, 'drawer', closeDrawer);
                                })}
                            </ul>
                        </div>

                        <div className={classes.DrawerSection}>
                            {drawerFooterTitle && (
                                <div className={classes.DrawerProfileName}>{resolveTitle(drawerFooterTitle, context)}</div>
                            )}

                            <div className={classes.DrawerFooterLinks}>
                                {drawerFooterItems.map((item) => {
                                    if (item.type === 'group' || item.type === 'dropdown') {
                                        return renderDesktopGroup(item, context, closeDrawer);
                                    }

                                    if (item.type === 'button') {
                                        return (
                                            <button
                                                key={item.key}
                                                type="button"
                                                className={classes.DrawerButton}
                                                onClick={logout}
                                            >
                                                <Icon className={classes.IconLeft} icon={item.icon} size={'18px'} />
                                                <span className={`${classes.LinkText} ${classes.ProfileText}`}>
                                                    {resolveTitle(item.title, context)}
                                                </span>
                                            </button>
                                        );
                                    }

                                    return <React.Fragment key={item.key}>{renderNavContent(item, context, 'drawer', closeDrawer)}</React.Fragment>;
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default withAuth(HeaderMenu);



// WEBPACK FOOTER //
// ./src/Components/HeaderMenu/index.js